
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Edit,
  Store,
  Phone,
  Mail,
  Globe,
  CheckCircle,
  XCircle,
  Calendar,
  Star,
  User,
  Users,
  Package,
  Tag,
  MapPin
} from 'lucide-react'
import useMerchantStore from '@/stores/merchantStore'
import { userService, User as UserType } from '@/services/userService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { GoogleMapsStaticMap, GoogleMapsConfigProvider } from '@/components/maps'
import { getImageUrl } from '@/config/imageConfig'

const MerchantDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const {
    currentMerchant,
    loading,
    error,
    fetchMerchantById,
    clearCurrentMerchant
  } = useMerchantStore()

  const [user, setUser] = useState<UserType | null>(null)
  const [userLoading, setUserLoading] = useState(false)
  const [userError, setUserError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchMerchantById(id)
    }

    return () => {
      clearCurrentMerchant()
    }
  }, [id])

  // Load user data when merchant is loaded
  useEffect(() => {
    const loadUser = async () => {
      if (!currentMerchant?.user_id) {
        setUser(null)
        return
      }
      
      setUserLoading(true)
      setUserError(null)
      try {
        const userData = await userService.getById(currentMerchant.user_id)
        setUser(userData)
      } catch (err) {
        setUserError('Impossible de charger les informations de l\'utilisateur')
      } finally {
        setUserLoading(false)
      }
    }

    loadUser()
  }, [currentMerchant?.user_id])

  if (loading && !currentMerchant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du commerçant...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Erreur"
          breadcrumbs={[
            { label: 'Commerçants', href: '/merchants' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/merchants"
        />
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!currentMerchant) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Commerçant non trouvé"
          breadcrumbs={[
            { label: 'Commerçants', href: '/merchants' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/merchants"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Store className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Commerçant non trouvé</h3>
              <p className="text-gray-500 mt-1">Le commerçant demandé n'existe pas ou a été supprimé.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const merchant = currentMerchant

  const breadcrumbs = [
    { label: 'Commerçants', href: '/merchants' },
    { label: merchant.business_name, href: '#' }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-5 w-5" />,
      onClick: () => navigate(`/merchants/${merchant.merchant_id}/edit`),
      variant: 'default' as const
    }
  ]

  const status = merchant.is_active
    ? { label: 'Actif', variant: 'success' as const }
    : { label: 'Inactif', variant: 'destructive' as const }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={merchant.business_name}
        breadcrumbs={breadcrumbs}
        backUrl="/merchants"
        actions={actions}
        status={status}
      />

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Business Information */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Informations du commerce">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {merchant.logo_url ? (
                    <img
                      src={getImageUrl(`/uploads/merchants/${merchant.logo_url}`)}
                      alt={merchant.business_name || 'Logo commerçant'}
                      className="h-20 w-20 rounded-xl object-cover border border-gray-200 bg-gray-50"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement | null
                        if (fallback) fallback.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  <div
                    className="h-20 w-20 rounded-xl bg-primary-100 items-center justify-center"
                    style={{ display: merchant.logo_url ? 'none' : 'flex' }}
                  >
                    <Store className="h-10 w-10 text-primary-600" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {merchant.business_name}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{merchant.business_type}</Badge>
                    <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                      {merchant.logo_url ? 'Logo personnalisé' : 'Aucun logo'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Description</h3>
                <p className="mt-1 text-gray-900 whitespace-pre-line">
                  {merchant.business_description || 'Aucune description fournie'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Numéro de TVA/TAX ID</h3>
                  <p className="mt-1 text-gray-900">
                    {merchant.tax_id || 'Non renseigné'}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">ID Commerçant</h3>
                  <p className="mt-1 font-mono text-sm text-gray-900">
                    {merchant.merchant_id}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Organisation</h3>
                  <p className="mt-1 text-gray-900">
                    {merchant.organization_name || 'Non spécifié'}
                  </p>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Informations de contact">
            <div className="space-y-4">
              <div className="flex items-start">
                <Phone className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Téléphone</h3>
                  <p className="mt-1 text-gray-900">{merchant.phone}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Email</h3>
                  <p className="mt-1 text-gray-900">{merchant.email}</p>
                </div>
              </div>
              
              {merchant.website && (
                <div className="flex items-start">
                  <Globe className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Site web</h3>
                    <a 
                      href={merchant.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-1 text-primary-600 hover:text-primary-700"
                    >
                      {merchant.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </DetailSection>

          {/* Address Information */}
          <DetailSection title="Adresse du commerce">
            <div className="space-y-4">
              {merchant.address_line1 || merchant.city || merchant.postal_code || merchant.country ? (
                <>
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                    <div className="space-y-2">
                      {merchant.address_line1 && (
                        <p className="text-gray-900">{merchant.address_line1}</p>
                      )}
                      {merchant.address_line2 && (
                        <p className="text-gray-900">{merchant.address_line2}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {merchant.city && (
                          <span className="text-gray-900">{merchant.city}</span>
                        )}
                        {merchant.state && (
                          <span className="text-gray-900">
                            {merchant.city ? ', ' : ''}{merchant.state}
                          </span>
                        )}
                        {merchant.postal_code && (
                          <span className="text-gray-900">
                            {(merchant.city || merchant.state) ? ' ' : ''}{merchant.postal_code}
                          </span>
                        )}
                        {merchant.country && (
                          <span className="text-gray-900">
                            {(merchant.city || merchant.state || merchant.postal_code) ? ', ' : ''}{merchant.country}
                          </span>
                        )}
                      </div>
                      {(merchant.latitude || merchant.longitude) && (
                        <div className="mt-2 text-sm text-gray-500">
                          {merchant.latitude && merchant.longitude ? (
                            <span>Coordonnées: {merchant.latitude}, {merchant.longitude}</span>
                          ) : merchant.latitude ? (
                            <span>Latitude: {merchant.latitude}</span>
                          ) : (
                            <span>Longitude: {merchant.longitude}</span>
                          )}
                        </div>
                      )}
                      {merchant.is_primary && (
                        <div className="mt-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            Adresse principale
                          </Badge>
                        </div>
                      )}

                      {/* Google Map Display */}
                      {merchant.latitude && merchant.longitude && (
                        <div className="mt-4">
                          <div className="text-sm font-medium text-gray-700 mb-2">Localisation sur la carte</div>
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <GoogleMapsConfigProvider
                              config={{
                                apiKey: 'AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ',
                                libraries: ['places'],
                                language: 'fr'
                                // region removed to allow worldwide search
                              }}
                            >
                              <GoogleMapsStaticMap
                                latitude={merchant.latitude}
                                longitude={merchant.longitude}
                                zoom={15}
                                width={400}
                                height={200}
                                markerColor="red"
                                className="rounded"
                                apiKey="AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ"
                              />
                            </GoogleMapsConfigProvider>
                            <div className="mt-2 text-xs text-gray-500">
                              Coordonnées: {Number(merchant.latitude)?.toFixed(6)}, {Number(merchant.longitude)?.toFixed(6)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center text-gray-500">
                  <MapPin className="h-5 w-5 mr-2" />
                  <p>Aucune adresse renseignée</p>
                </div>
              )}
            </div>
          </DetailSection>
        </div>

        {/* Right column - Statistics and Metadata */}
        <div className="space-y-6">
          <DetailSection title="Statistiques">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-yellow-400 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Note moyenne</h3>
                    <p className="text-2xl font-bold text-gray-900">
                      {merchant.average_rating ? Number(merchant.average_rating).toFixed(1) : '0.0'}
                      <span className="text-sm text-gray-500">/5</span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Nombre d'avis</h3>
                <p className="text-2xl font-bold text-gray-900">{merchant.review_count || 0}</p>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Métadonnées">
            <div className="space-y-4">
              <div className="flex items-start">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Créé le</h3>
                  <p className="mt-1 text-gray-900">
                    {new Date(merchant.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Dernière mise à jour</h3>
                  <p className="mt-1 text-gray-900">
                    {new Date(merchant.updated_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <User className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Utilisateur</h3>
                  {userLoading ? (
                    <div className="mt-1">
                      <div className="animate-pulse h-4 w-32 bg-gray-200 rounded"></div>
                    </div>
                  ) : userError ? (
                    <div className="mt-1">
                      <p className="text-sm text-gray-900">{merchant.user_id}</p>
                      <p className="text-xs text-red-500">{userError}</p>
                    </div>
                  ) : user ? (
                    <div className="mt-1">
                      <Link
                        to={`/users/${user.user_id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {user.first_name} {user.last_name}
                      </Link>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {user.email}
                      </p>
                      <p className="text-xs font-mono text-gray-400 mt-0.5">
                        ID: {merchant.user_id}
                      </p>
                    </div>
                  ) : (
                    <p className="mt-1 text-gray-900">
                      {merchant.user_id}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Actions rapides">
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/merchants/${merchant.merchant_id}/edit`)}
              >
                <Edit className="h-5 w-5 mr-2" />
                Modifier le commerçant
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/mes-utilisateurs?merchantId=${merchant.merchant_id}`)}
              >
                <Users className="h-5 w-5 mr-2" />
                Gérer les utilisateurs
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/products?merchantId=${merchant.merchant_id}`)}
              >
                <Package className="h-5 w-5 mr-2" />
                Voir les produits
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/offers?merchantId=${merchant.merchant_id}`)}
              >
                <Tag className="h-5 w-5 mr-2" />
                Voir les offres
              </Button>
              
              <Button
                variant={merchant.is_active ? "destructive" : "default"}
                className="w-full justify-start"
                onClick={() => {
                  if (window.confirm(`Êtes-vous sûr de vouloir ${merchant.is_active ? 'désactiver' : 'activer'} ce commerçant ?`)) {
                    // TODO: Implement toggle active status
                  }
                }}
              >
                {merchant.is_active ? (
                  <>
                    <XCircle className="h-5 w-5 mr-2" />
                    Désactiver le commerçant
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Activer le commerçant
                  </>
                )}
              </Button>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default MerchantDetails
